// ============================================================================
// Projects as rows a person can read and pick. Item #542 part 2, 2026-10-19.
//
// ONE fetch, used by two places so they cannot drift:
//   - SmartSelect's `project` entity (the Change project picker on an invoice
//     line — TCSD-0171: SmartSelect is THE picker, so projects are added to it
//     rather than a second picker being built);
//   - the invoice page's Project column, which names the project each line
//     bills.
//
// Flat fetches only (no PostgREST embeds — the SmartSelect pattern): projects,
// then their service lines, then the catalog's display names. The label is the
// same one every project card prints (projectServiceLabel).
// ============================================================================

import { supabase } from '../../../shared/supabase';
import { PROJECT_STAGE_LABELS, type ProjectServiceLine, type ProjectStage } from '../../../shared/types';
import { projectServiceLabel } from './project-service-label';

export interface ProjectRow {
  project_id: string;
  facility_id: string | null;
  facility_name: string | null;
  stage: string | null;
  invoice_id: string | null;
  bill_amount: number | null;
  /** The project's work in words, e.g. "Strip & Wax + Deep Clean". */
  label: string;
}

/** "Ready to Invoice", "Pending PO" … — the labels every project card uses. */
export function projectStageLabel(stage: string | null | undefined): string {
  if (!stage) return 'No stage';
  return PROJECT_STAGE_LABELS[stage as ProjectStage] ?? stage.replace(/_/g, ' ');
}

/** What a line or a picker row calls a project: "PRJ-2026-06-0070 · Deep Clean". */
export function projectRowName(row: Pick<ProjectRow, 'project_id' | 'label'>): string {
  return `${row.project_id} · ${row.label}`;
}

// .in() rides in the URL; past this many ids, read every line instead.
const IN_LIMIT = 150;

export async function fetchProjectRows(filter: {
  facilityId?: string;
  projectIds?: string[];
}): Promise<ProjectRow[]> {
  let q = supabase
    .from('projects')
    .select('project_id, facility_id, facility_name, stage, invoice_id, bill_amount, service_type_raw, title')
    .order('project_id', { ascending: false });
  if (filter.facilityId) q = q.eq('facility_id', filter.facilityId);
  if (filter.projectIds) {
    if (filter.projectIds.length === 0) return [];
    q = q.in('project_id', filter.projectIds);
  }
  const { data, error } = await q;
  if (error) throw error;
  type P = {
    project_id: string;
    facility_id: string | null;
    facility_name: string | null;
    stage: string | null;
    invoice_id: string | null;
    bill_amount: number | null;
    service_type_raw: string | null;
    title: string | null;
  };
  const projects = (data ?? []) as P[];
  if (projects.length === 0) return [];

  const ids = projects.map((p) => p.project_id);
  let lq = supabase
    .from('project_services')
    .select('project_id, service_code, scope_text, line_order, line_role');
  if (ids.length <= IN_LIMIT) lq = lq.in('project_id', ids);
  const [{ data: lines, error: le }, { data: catalog, error: ce }] = await Promise.all([
    lq,
    supabase.from('service_catalog').select('service_code, display_name, division_id'),
  ]);
  if (le) throw le;
  if (ce) throw ce;

  type C = { service_code: string; display_name: string | null; division_id: string | null };
  const byCode = new Map(((catalog ?? []) as C[]).map((c) => [c.service_code, c]));
  type L = {
    project_id: string;
    service_code: string | null;
    scope_text: string | null;
    line_order: number | null;
    line_role: string;
  };
  const linesByProject = new Map<string, ProjectServiceLine[]>();
  for (const l of (lines ?? []) as L[]) {
    const c = l.service_code ? byCode.get(l.service_code) : undefined;
    const line = {
      ...l,
      service_catalog: c ? { display_name: c.display_name, division_id: c.division_id } : null,
    } as unknown as ProjectServiceLine;
    const list = linesByProject.get(l.project_id) ?? [];
    list.push(line);
    linesByProject.set(l.project_id, list);
  }

  return projects.map((p) => ({
    project_id: p.project_id,
    facility_id: p.facility_id,
    facility_name: p.facility_name,
    stage: p.stage,
    invoice_id: p.invoice_id,
    bill_amount: p.bill_amount,
    label:
      projectServiceLabel({
        service_type_raw: p.service_type_raw,
        project_services: linesByProject.get(p.project_id) ?? [],
      }) ??
      p.title ??
      'Project work',
  }));
}
